import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import styles from './css/index.module.scss';
import CompositionScheme from './components/CompositionScheme';
import CompositionParams from './components/CompositionParams';
import SidebarHint from './components/SidebarHint';
import LayerSettings from './components/LayerSettings';
import ResultsPanel from './components/ResultsPanel';
import ResultsTable from './components/ResultsTable';
import Button from '../../ui-kit/Button';
import RefreshIcon from '../../assets/icons/refresh.svg';
import useSoundproofing from '../../hooks/useSoundproofing';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import { windowWidthAtom } from '../../atoms/globalAtoms';
import { SOUNDPROOFING_SIDEBAR_MODES } from '../../atoms/soundproofingAtoms';

function Soundproofing() {
  const { t } = useTranslations();
  const resultsRef = useRef(null);
  const [scrollToResultsRequest, setScrollToResultsRequest] = useState(0);
  const [windowWidth] = useAtom(windowWidthAtom);
  const isSmallScreen = windowWidth <= 900;

  const {
    selectedLayer,
    sidebarMode,
    isCompositionSet,
    isResultsVisible,
    resultsLoading,
    showResults,
    onResetConfigurationClick,
  } = useSoundproofing();

  const onShowResultsClick = () => {
    showResults().then();
    setScrollToResultsRequest((prev) => prev + 1);
  };

  useEffect(() => {
    if (!scrollToResultsRequest) return;
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollToResultsRequest]);

  const renderSidebarContent = () => {
    if (selectedLayer) return <LayerSettings />;
    if (sidebarMode === SOUNDPROOFING_SIDEBAR_MODES.results && isResultsVisible) return <ResultsPanel />;
    return <SidebarHint isCompositionSet={isCompositionSet} />;
  };

  return (
    <div className={styles.soundproofing}>
      {!isSmallScreen && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarInner}>
            {renderSidebarContent()}
          </div>
        </aside>
      )}

      <div className={styles.main}>
        <div className={styles.topRow}>
          <div className={classNames(styles.refresh, 'pressable')} onClick={onResetConfigurationClick}>
            <img src={RefreshIcon} alt="<" />
            <p>{t(DICTIONARY.refreshConfiguration)}</p>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.scheme}>
            <CompositionScheme />
          </div>

          {isSmallScreen && (
            <div className={styles.mobileHint}>
              <SidebarHint isCompositionSet={isCompositionSet} isMobile />
            </div>
          )}

          <div className={styles.params}>
            <CompositionParams />
          </div>

          <div className={styles.showResultsButton}>
            <Button
              width={isSmallScreen ? '100%' : 223}
              text={t(DICTIONARY.soundproofingPage.showResults)}
              isDisabled={resultsLoading}
              onClick={onShowResultsClick}
            />
          </div>
        </div>

        {isResultsVisible && (
          <div className={classNames(styles.results, 'blueHorizontalScroll')} ref={resultsRef}>
            <div className={styles.resultsInner}>
              {isSmallScreen && <ResultsPanel isMobile />}
              <ResultsTable isMobile={isSmallScreen} />
              <p className={styles.legalText}>{t(DICTIONARY.footerDescription)}</p>
            </div>
          </div>
        )}
      </div>

      {isSmallScreen && selectedLayer && (
        <div className={styles.bottomSheet}>
          <LayerSettings isBottomSheet />
        </div>
      )}
    </div>
  );
}

export default Soundproofing;
